import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent
  }
}

/**
 * Hook to handle PWA install prompt (Add to Home Screen)
 * Returns install state and prompt function
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if user previously dismissed the prompt
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('pwa-install-dismissed') === 'true'
    }
    return false
  })

  // Check if already installed
  useEffect(() => {
    // Check if running in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Checking installed state on mount
      setIsInstalled(true)
      return
    }

    // iOS Safari check
    // @ts-expect-error - navigator.standalone is iOS-specific
    if (navigator.standalone === true) {
      setIsInstalled(true)
    }
  }, [])

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      event.preventDefault()
      // Store the event for later use
      setDeferredPrompt(event)
      setIsInstallable(true)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // Trigger the install prompt
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false
    }

    // Show the prompt
    await deferredPrompt.prompt()

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice

    // Clear the deferred prompt
    setDeferredPrompt(null)
    setIsInstallable(false)

    if (outcome === 'accepted') {
      setIsInstalled(true)
      return true
    }

    return false
  }, [deferredPrompt])

  // Dismiss the prompt (user chose not to install)
  const dismissPrompt = useCallback(() => {
    setIsDismissed(true)
    localStorage.setItem('pwa-install-dismissed', 'true')
  }, [])

  // Reset dismissed state (allow showing prompt again)
  const resetDismissed = useCallback(() => {
    setIsDismissed(false)
    localStorage.removeItem('pwa-install-dismissed')
  }, [])

  return {
    isInstallable: isInstallable && !isDismissed && !isInstalled,
    isInstalled,
    isDismissed,
    promptInstall,
    dismissPrompt,
    resetDismissed,
  }
}
